import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ElementRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  DisableSettingsEdit,
  SettingsEditState,
  UnSavedSettingsEdit,
} from '@ops/services';
import {
  AdditionalSocialMediaChannels,
  PROFILE_DEFAULT_OPTIONS,
  PROFILE_FORM_FIELD_NAMES,
  SessionManagerService,
  User,
  UserEdit,
  UserEditSave,
  UsersService,
} from '@ops/shared-types';
import { Observable, Subscription } from 'rxjs';
// import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ops-profile-settings',
  templateUrl: './ops-profile-settings.component.html',
  styleUrl: './ops-profile-settings.component.scss',
})
export class OpsProfileSettingsComponent implements OnInit, OnDestroy {
  editState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getEditState
  );

  saveState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getSaveState
  );

  subs: Array<Subscription> = [];

  maxBirthDate = new Date();

  constructor(
    private store: Store,
    private sessionManager: SessionManagerService,
    private usersService: UsersService,
    private element: ElementRef, // private translate: TranslateService,
    private translateService: TranslateService
  ) {
    // this.translate.setDefaultLang('en');
    // this.translate.use('en');
  }

  ngOnInit(): void {
    const eState = this.editState$.subscribe((isEditing: any) => {
      this.isPreview = !isEditing;
      this.errorsTrack = {};

      if (!this.user) {
        this.user = this.sessionManager.getUser();
      }

      if (this.user) {
        this.userEdit = {
          ...PROFILE_FORM_FIELD_NAMES,
          ...this.user,
        };

        this.userInitials =
          (this.user?.firstName?.[0]?.toUpperCase() ?? '') +
          (this.user?.lastName?.[0]?.toUpperCase() ?? '');

        if (this.user.additionalSocialMediaChannels.length) {
          this.userEdit.additionalSocialMediaChannels = [
            ...this.user.additionalSocialMediaChannels,
          ];
        } else {
          this.userEdit.additionalSocialMediaChannels = [];
        }
      } else {
        this.userEdit = { ...PROFILE_FORM_FIELD_NAMES };
        this.userEdit.additionalSocialMediaChannels = [];
      }
    });

    const sState = this.saveState$.subscribe((saveChanges: any) => {
      if (saveChanges === true) {
        //    console.log('SAVE CHANGES');

        const errorFields = Object.keys(this.errorsTrack);
        if (errorFields.length === 0) {
          this.saveUserChanges();
          this.store.dispatch(new UnSavedSettingsEdit());
        } else {
          const errorsList = errorFields.map((errorKey) => {
            return (
              '\n' + errorKey.toUpperCase() + ': ' + this.errorsTrack[errorKey]
            );
          });

          const joinMessage = errorsList.join('');

          this.store.dispatch(new UnSavedSettingsEdit());

          window.alert(
            this.translateService.instant('windowAlert.opsProfileSettings.invalidFields', { errors: joinMessage })
          );
        }
      }
    });

    const uSub = this.sessionManager
      .getUserObservable()
      .subscribe((user: User) => {
        this.user = { ...PROFILE_FORM_FIELD_NAMES, ...user };
        if (this.user) {
          this.userEdit = { ...user };
          this.userInitials =
            (user?.firstName?.[0]?.toUpperCase() ?? '') +
            (user?.lastName?.[0]?.toUpperCase() ?? '');
        } else {
          this.userEdit = { ...PROFILE_FORM_FIELD_NAMES };
        }
      });
    this.subs.push(uSub);
    this.subs.push(eState);
    this.subs.push(sState);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  saveUserChanges() {
    const userChanges: UserEditSave = {
      ...this.user,
      ...this.userEdit,
    };

    delete userChanges['projectRole'];
    delete userChanges['roleCompanies'];
    delete userChanges['roleProjects'];
    delete userChanges['initials'];
    delete userChanges['email'];
    delete userChanges['creativeIndustrySector'];
    delete userChanges['isAdmin'];
    delete userChanges['marketDomain'];
    delete userChanges['permissions'];
    delete userChanges['companies'];
    delete userChanges['deletedAt'];
    delete userChanges['_id'];
    delete userChanges['password'];
    delete userChanges['isVerified'];
    delete userChanges['createdAt'];
    delete userChanges['updatedAt'];
    delete userChanges['__v'];
    delete userChanges['accessToken'];
    delete userChanges['id'];
    delete userChanges['collaborator'];

    if (!userChanges['dateOfBirth']) {
      userChanges['dateOfBirth'] = null;
    }

    const sub = this.usersService.updateUser(userChanges).subscribe(
      (userUpdated: User) => {
        this.sessionManager.setCurrentUser(userUpdated);
        this.store.dispatch(new DisableSettingsEdit());
      },
      (error) => {
        //    console.log('Error', error);
        if (error?.error?.message) {
          const errorsList = error?.error?.message.split('.');
          const errorDescription =
            'Update Error\n\nSome fields are invalid...\nPlease check fields:\n\n';

          const fullMessage = this.translateService.instant('windowAlert.opsProfileSettings.updateError', { errors: errorsList.join('.\n') });
          window.alert(fullMessage);
        }
      }
    );
    this.subs.push(sub);
  }

  user?: User;
  userEdit: UserEdit = { ...PROFILE_FORM_FIELD_NAMES };

  errorsTrack = {};

  userInitials = 'OI';

  genderOptions = ['Male', 'Female', 'Other'];
  dayOptions = Array.from({ length: 31 }, (_, i) => i + 1); // Days 1-31
  monthOptions = Array.from({ length: 12 }, (_, i) => i + 1); // Months 1-12
  yearOptions = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  ); // Years

  professionalRolesList = PROFILE_DEFAULT_OPTIONS.professionalRolesList;
  // projectRolesList = PROFILE_DEFAULT_OPTIONS.projectRoles;
  businessTypes = PROFILE_DEFAULT_OPTIONS.businessTypes;
  creativeIndustrySectorsList =
    PROFILE_DEFAULT_OPTIONS.creativeIndustrySectorsList;
  marketDomainsList = PROFILE_DEFAULT_OPTIONS.marketDomainsList;
  fieldOfExpertises = PROFILE_DEFAULT_OPTIONS.fieldOfExpertises;
  workStyleAndPreferences = PROFILE_DEFAULT_OPTIONS.workStylePreferences;
  digitalTools = PROFILE_DEFAULT_OPTIONS.digitalTools;
  preferredCommunicationMethods =
    PROFILE_DEFAULT_OPTIONS.preferredCommunicationMethods;
  preferredCommunicationStyles =
    PROFILE_DEFAULT_OPTIONS.preferredCommunicationStyles;
  teamPlays = PROFILE_DEFAULT_OPTIONS.teamPlays;
  workToolsList = PROFILE_DEFAULT_OPTIONS.workTools;
  interestsAndInspirationsList =
    PROFILE_DEFAULT_OPTIONS.interestsAndInspirations;
  favoriteArtistsArtworksCultures =
    PROFILE_DEFAULT_OPTIONS.favoriteArtistsArtworksCultures;
  leisureActivities = PROFILE_DEFAULT_OPTIONS.leisureActivities;
  starSigns = PROFILE_DEFAULT_OPTIONS.starSigns;
  personalities = PROFILE_DEFAULT_OPTIONS.personalities;
  zodiacSigns = PROFILE_DEFAULT_OPTIONS.zodiacSigns;
  ennagrams = PROFILE_DEFAULT_OPTIONS.ennagrams;

  isPreview = true;

  backgroundImageUrl?: string | ArrayBuffer = '';

  addSocialMediaChannel(_event: any) {
    //    console.log('Avatar change triggered');
  }

  get userProfileImage() {
    return this.backgroundImageUrl
      ? this.backgroundImageUrl
      : this.userEdit.profilePhotoUrl;
  }

  onAdditionalChannelChange(value: string, channelName: string) {
    const adItem = this.userEdit.additionalSocialMediaChannels.find(
      (adItem) => {
        return adItem.name === channelName;
      }
    );
    adItem.url = value;
  }

  removeAdChannel(additionalChannel: AdditionalSocialMediaChannels) {
    //    console.log('REMOVE CHANNEL', additionalChannel.name);

    const addChannels = [...this.userEdit.additionalSocialMediaChannels];
    this.userEdit.additionalSocialMediaChannels = [];

    this.userEdit.additionalSocialMediaChannels = [
      ...addChannels.filter(
        (adCh) =>
          !(
            adCh.name === additionalChannel.name &&
            adCh.url === additionalChannel.url
          )
      ),
    ];

    this.errorsTrack = {};
  }

  setFocusOnCreatedChannel() {
    const itemIdToFocus =
      this.userEdit.additionalSocialMediaChannels.length - 1;

    if (itemIdToFocus >= 0 && !this.isPreview) {
      const itemId = `ad-channel-${itemIdToFocus}`;

      document.getElementById(itemId).getElementsByTagName('input')[0].focus();
    }
  }

  createNewChannel(channelName: string) {
    const exists = this.userEdit.additionalSocialMediaChannels.find(
      (chNameItem) => {
        return chNameItem.name === channelName;
      }
    );

    if (exists) {
      const channelName = exists.name[exists.name.length - 1];
      const numValue = isNaN(parseInt(channelName))
        ? ' 1'
        : ' ' + (parseInt(channelName) + 1);
      const channelNameNew = exists.name.split(' ')[0];
      const newValName = channelNameNew + ' ' + numValue;
      return this.createNewChannel(newValName);
    }
    this.userEdit.additionalSocialMediaChannels.push({
      name: channelName,
      url: '',
    });

    setTimeout(() => {
      this.setFocusOnCreatedChannel();
    }, 150);

    //    console.log('CREATE NEW CHANNEL ', channelName);
  }

  readSVGFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }

  convertSVGToImage(svgText, format) {
    return new Promise((resolve) => {
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL(format));
      };

      img.src = url;
    });
  }

  base64ToBlob(base64, mimeType) {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mimeType });
  }

  base64ToFile(base64, fileName, mimeType) {
    const blob = this.base64ToBlob(base64, mimeType);
    return new File([blob], fileName, { type: mimeType });
  }

  async uploadFile(event: Event) {
    const allowedFormats = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/svg+xml',
      'image/bmp',
      'image/gif',
      'image/webp',
    ];
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      if (!allowedFormats.includes(file.type)) {
        window.alert(
          this.translateService.instant('windowAlert.opsProfileSettings.invalidFileFormat')
        );
        (event.target as HTMLInputElement).value = '';
        return;
      }

      const imageUrlFinal = [];

      if (file && file.type === 'image/svg+xml') {
        const svgData = await this.readSVGFile(file);
        const imageUrl = await this.convertSVGToImage(svgData, 'image/png'); // Change to 'image/jpeg' for JPEG format
        // document.getElementById('outputImage').src = imageUrl;

        const convertedFile = this.base64ToFile(
          imageUrl,
          new Date().getTime() + '.png',
          'image/png'
        );

        imageUrlFinal.push(convertedFile);
      } else if (file) {
        imageUrlFinal.push(file);
      }

      if (imageUrlFinal.length) {
        const sub = this.usersService.uploadAvatar(imageUrlFinal[0]).subscribe(
          (updatedUser: any) => {
            this.userEdit.profilePhotoUrl = updatedUser.profilePhotoUrl;
            //    console.log('uploadedAvatar', JSON.stringify(uploadedAvatar));
            // this.sessionManager.setCurrentUser(userUpdated);
            // this.store.dispatch(new DisableSettingsEdit());
            (event.target as HTMLInputElement).value = '';
          },
          (error) => {
            //    console.log('Error', error);
            if (error?.error?.message) {
              const errorsList = error?.error?.message.split('.');
              const errorDescription =
                'Update Error\n\nSome fields are invalid...\nPlease check fields:\n\n';

              const fullMessage = this.translateService.instant('windowAlert.opsProfileSettings.updateError', { errors: errorsList.join('.\n') });
              window.alert(fullMessage);
            }
            (event.target as HTMLInputElement).value = '';
          }
        );
        this.subs.push(sub);
        const reader = new FileReader();
        reader.onload = (e) => {
          this.backgroundImageUrl = reader.result; // Store the file URL to use it as a background
        };
        reader.readAsDataURL(imageUrlFinal[0]); // Read the file as a data URL
      }
      (event.target as HTMLInputElement).value = '';
    }
  }

  onAvatarChange(_event?: MouseEvent) {
    //    console.log('Avatar change triggered');
    this.element.nativeElement.getElementsByClassName('file-input')[0].click();
  }

  onSelectChange(selectValue: string, field?: string): void {
    this.userEdit[field] = selectValue;
  }

  updateFullDate(date: string) {
    //    console.log('DATE OF BIRTH', date);
    this.userEdit.dateOfBirth = date ?? null;
  }

  onErrorHandler(error: string, field?: string) {
    if (!error || error === '') {
      delete this.errorsTrack[field];
    } else {
      this.errorsTrack[field] = error;
    }
  }

  onInputChange(newValue: string, field?: string): void {
    this.userEdit[field] = newValue;
  }
}
